import React from "react";
import { Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import moment from "moment";

import { tOrderPayoutResponse } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import * as utilsDb from "src/db/utils.db";
import * as orderDb from "src/db/order.db";
import { PdStack, PdText } from "src/components/layout";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { kStyleConst } from "src/kStyle";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableRowMobile,
  PdTableRowMobileButton,
  PdTableRowMobileLabel,
  PdTableRowMobilePanel,
  PdTableRowMobileValue,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { formatMoney, formatPercentage } from "src/util/gFormatting";

import { kTablePageSize } from "src/util/constants";
import { getColorSchemeFromOrderStatus } from "src/util/ui";
import { tPressableState } from "src/components/types";
import { NavigationProp, ParamListBase, useIsFocused } from "@react-navigation/native";
import { kRoute } from "src/kRoute";
import {
  changeFilterNextPage,
  changeFilterPayoutStatus,
  changeFilterPrevPage,
  resetFilters,
} from "src/redux/filters/actions";
import { useAppDispatch } from "src/redux";
import { PdBadge, PdBadgeLabel, PdTab, PdTabLabel } from "src/components/v2/layout";
import { PdRow } from "src/components/v2/ui/core";

type tTableState = {
  data: tOrderPayoutResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

export type tUpdatedPayload = {
  isSelected?: boolean;
};

export type tUpdated = {
  [rowId: string]: tUpdatedPayload;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const formatOrderMobileDate = (dateInMilliseconds: number) => moment(dateInMilliseconds).format("MMM DD, YYYY");

const useTableColumnsOrders = () => {
  const userType = useSelector((state) => state.UserReducer.profile.accountType);
  return React.useMemo(() => {
    const columns: tColumn<tOrderPayoutResponse>[] = [
      {
        id: "payoutStatus",
        header: "Payout Date",
        accessor: (originalRow) => (originalRow.payoutStatus === "paid" ? "Paid" : "Unpaid"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "payoutAmount",
        header: "Payout Amount",
        accessor: (originalRow) => formatMoney(originalRow.payoutAmount),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      ...(userType === "rep" ? [commissionFeeColumn, vendorNameColumn] : []),
      {
        id: "buyerName",
        header: "Customer",
        accessor: (originalRow) => originalRow.buyerName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      ...(userType === "brand" ? [sourceColumn] : []),
      {
        id: "totalAmount",
        header: userType === "brand" ? "Order Subtotal" : "Order Total",
        accessor: (originalRow) => formatMoney(originalRow.totalAmount),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      ...(userType === "brand" ? [marketplaceTakeAmountColumn, repTakeAmountColumn, shippingAmountColumn] : []),
      {
        id: "status",
        header: "Order Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ value, row }) => (
          <PdBadge colorScheme={getColorSchemeFromOrderStatus(row.status)}>
            <PdBadgeLabel>{value}</PdBadgeLabel>
          </PdBadge>
        ),
        style: styles.tableColumnMd,
      },
      {
        id: "shippedAt",
        header: userType === "brand" ? "Payout Date" : "Ship Date",
        accessor: (originalRow) => (originalRow.shippedAtInMillis ? formatDate(originalRow.shippedAtInMillis) : "-"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "number",
        header: "Order #",
        accessor: (originalRow) => originalRow.number,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
    ];
    return columns;
  }, [userType]);
};

const OrderMobileRow: React.FC<{
  item: tRowInstance<tOrderPayoutResponse>;
  onPress?: (item: tRowInstance<tOrderPayoutResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <PdTableRowMobile>
      <PdTableRowMobileButton>
        <PdText style={styles.flex1} variant="body-sm">
          {`#${item.number}`}
        </PdText>
        <PdBadge colorScheme={getColorSchemeFromOrderStatus(item.status)} style={styles.ml2}>
          <PdBadgeLabel>{item.status}</PdBadgeLabel>
        </PdBadge>
      </PdTableRowMobileButton>
      <PdTableRowMobilePanel>
        <TouchableOpacity style={styles.pdTableRowMobileContent} onPress={handleOnPress}>
          <PdStack gap={1} direction="vertical">
            <PdText variant="body-sm">{formatOrderMobileDate(item.shippedAtInMillis)}</PdText>
            <PdRow>
              <PdTableRowMobileLabel>Customer</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{item.buyerName}</PdTableRowMobileValue>
            </PdRow>
            <PdRow>
              <PdTableRowMobileLabel>Payout Amount</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{formatMoney(item.totalAmount)}</PdTableRowMobileValue>
            </PdRow>
          </PdStack>
        </TouchableOpacity>
      </PdTableRowMobilePanel>
    </PdTableRowMobile>
  );
};

const OrderRow: React.FC<{
  item: tRowInstance<tOrderPayoutResponse>;
  onPress?: (item: tRowInstance<tOrderPayoutResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow isPressed={pressed} isHovered={hovered}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const P03_04_Payouts: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const isFocused = useIsFocused();
  const dispatch = useAppDispatch();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rAccountType = useSelector((state) => state.UserReducer.profile.accountType);
  const filters = useSelector((state) => state.FiltersReducer.payouts);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });
  const columns = useTableColumnsOrders();
  const tableInstance = useTable<tOrderPayoutResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "",
      sortDirection: "desc",
    },
  });
  const isMobile = useIsMobile();

  const navigateToOrderView = React.useCallback(
    (row: tRowInstance<tOrderPayoutResponse>) => {
      navigation.navigate(kRoute.P03_01, { id: row.id });
    },
    [navigation],
  );

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "payouts", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "repMyBrands", value: tableState?.next ?? "" }));
  };

  const renderItem: React.FC<{ item: tRowInstance<tOrderPayoutResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <OrderMobileRow item={item} onPress={navigateToOrderView} />;
      return <OrderRow item={item} onPress={navigateToOrderView} />;
    },
    [isMobile, navigateToOrderView],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const params: orderDb.tQueryOrdersParams = { status: ["Pending", "Awaiting Fulfillment", "Shipped"] };
      if (filters.payoutStatus === "paid") params.status = "Shipped";
      if (filters.payoutStatus === "unpaid") params.status = ["Pending", "Awaiting Fulfillment"];
      if (rAccountType === "brand") params.brandId = rCompanyId;
      if (rAccountType === "rep") params.repId = rCompanyId;
      const query = await utilsDb.withPagination(orderDb.queryOrder, orderDb.documentOrder, {
        ...params,
        prev: filters.prev,
        next: filters.next,
        limit: kTablePageSize,
      });

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapOrderPayout(doc.data(), rAccountType));
        setTableState({
          data,
          isLoading: false,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [filters.next, filters.payoutStatus, filters.prev, rAccountType, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  return (
    <View
      style={[styles.flex1, isMobile ? [styles.pt4, styles.pb2, styles.mx4] : [styles.pt8, styles.pb4, styles.mx10]]}
    >
      <PdText variant="subheader" style={styles.mb4}>
        Payouts
      </PdText>
      <PdStack direction="horizontal" style={styles.mb2}>
        <PdTab
          isActive={!filters.payoutStatus}
          onPress={() => dispatch(changeFilterPayoutStatus({ pageName: "payouts" }))}
        >
          <PdTabLabel>All</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.payoutStatus === "paid"}
          onPress={() => dispatch(changeFilterPayoutStatus({ pageName: "payouts", value: "paid" }))}
        >
          <PdTabLabel>Paid</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.payoutStatus === "unpaid"}
          onPress={() => dispatch(changeFilterPayoutStatus({ pageName: "payouts", value: "unpaid" }))}
        >
          <PdTabLabel>Unpaid</PdTabLabel>
        </PdTab>
      </PdStack>
      <PdTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <View style={{ height: kStyleConst.kSpacing * 2 }} /> : null)}
        ListEmptyComponent={PdTableEmpty}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        isMobile={isMobile}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </View>
  );
};

export const P03_04 = React.memo(P03_04_Payouts);

const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  mb2: {
    marginBottom: kStyleConst.kSpacing * 2,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mx10: {
    marginHorizontal: kStyleConst.kSpacing * 10,
  },
  mx4: {
    marginHorizontal: kStyleConst.kSpacing * 4,
  },
  pb2: {
    paddingBottom: kStyleConst.kSpacing * 2,
  },
  pb4: {
    paddingBottom: kStyleConst.kSpacing * 4,
  },
  pdTableRowMobileContent: {
    padding: kStyleConst.kSpacing * 2,
    paddingBottom: kStyleConst.kSpacing * 3,
    paddingLeft: 45,
  },
  pt4: {
    paddingTop: kStyleConst.kSpacing * 4,
  },
  pt8: {
    paddingTop: kStyleConst.kSpacing * 8,
  },
  tableColumnLg: {
    width: 220,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 120,
  },
});

const vendorNameColumn: tColumn<tOrderPayoutResponse> = {
  id: "vendorName",
  header: "Brand",
  accessor: (originalRow) => originalRow.vendorName,
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnLg,
};

const commissionFeeColumn: tColumn<tOrderPayoutResponse> = {
  id: "commissionFee",
  header: "Commission %",
  accessor: (originalRow) => formatPercentage(originalRow.commissionFee),
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnSm,
};

const sourceColumn: tColumn<tOrderPayoutResponse> = {
  id: "source",
  header: "Order Source",
  accessor: (originalRow) => originalRow.source,
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnMd,
};

const marketplaceTakeAmountColumn: tColumn<tOrderPayoutResponse> = {
  id: "marketplaceTakeAmount",
  header: "The Main Tab",
  accessor: (originalRow) => formatMoney(originalRow.marketplaceTakeAmount),
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnMd,
};

const repTakeAmountColumn: tColumn<tOrderPayoutResponse> = {
  id: "repTakeAmount",
  header: "Rep Payout",
  accessor: (originalRow) => (originalRow.repTakeAmount ? formatMoney(originalRow.repTakeAmount) : "-"),
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnSm,
};

const shippingAmountColumn: tColumn<tOrderPayoutResponse> = {
  id: "shippingAmount",
  header: "Shipping",
  accessor: (originalRow) => (originalRow.shippingAmount ? formatMoney(originalRow.shippingAmount) : "-"),
  Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
  style: styles.tableColumnSm,
};
